import { useEffect, useRef, useState } from "react"
import { apiClient } from "@/lib/api"

// --- Config cache (fetched once per session, never blocks render) ---

const CONFIG_CACHE_KEY = "sf:intel-config"

interface GateConfig {
  ritual_enabled: boolean
  cooldown_minutes: number
}

const DEFAULTS: GateConfig = {
  ritual_enabled: true,
  cooldown_minutes: 30,
}

let configFetchStarted = false

function getCachedConfig(): GateConfig {
  try {
    const raw = sessionStorage.getItem(CONFIG_CACHE_KEY)
    if (raw) return JSON.parse(raw) as GateConfig
  } catch {}
  return DEFAULTS
}

function fetchConfigOnce(): void {
  if (configFetchStarted) return
  configFetchStarted = true
  apiClient.getIntelligenceGateConfig().then(res => {
    if (res.ok && res.config) {
      try {
        sessionStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({
          ritual_enabled: res.config.ritual_enabled,
          cooldown_minutes: res.config.cooldown_minutes,
        }))
      } catch {}
    }
  }).catch(() => {})
}

// --- Gating logic ---

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function seenKey(scope: string, propertyId: string): string {
  return `sf:intel-seen:${scope}:${propertyId}:${todayStr()}`
}

const COOLDOWN_KEY = "sf:intel-cooldown"

function shouldFireRitual(scope: string, propertyId: string, config: GateConfig): boolean {
  if (!config.ritual_enabled) return false
  try {
    if (sessionStorage.getItem(seenKey(scope, propertyId))) return false
    const ts = sessionStorage.getItem(COOLDOWN_KEY)
    if (ts && Date.now() - Number(ts) < config.cooldown_minutes * 60 * 1000) return false
    return true
  } catch {
    return false
  }
}

function markSeen(scope: string, propertyId: string): void {
  try {
    sessionStorage.setItem(seenKey(scope, propertyId), "1")
    sessionStorage.setItem(COOLDOWN_KEY, String(Date.now()))
  } catch {}
}

interface Options {
  scope: "overview" | "dashboard"
  propertyId: string | undefined
  isReady: boolean
}

/**
 * Gates the intelligence loading surface (AiLoadingCard) to a once-daily
 * branded loading state per property, with a configurable cross-property
 * cooldown.
 *
 * - Fires on the first cached visit of the day for a property/scope.
 * - The loading surface tracks GENUINE readiness only — there is no
 *   artificial hold. It shows while the data is actually loading and
 *   disappears the instant the data is ready.
 * - Computing states (genuine backend work) bypass this gate entirely —
 *   pages handle those inline with their own AiLoadingCard.
 * - Config is read from sessionStorage (cached from `/api/intelligence-gate`).
 *   Falls back to hardcoded defaults if not yet fetched.
 */
export function useIntelligenceGate({
  scope,
  propertyId,
  isReady,
}: Options): { showRitual: boolean } {
  const config = getCachedConfig()
  const markedRef = useRef(false)

  const [ritual] = useState(() =>
    propertyId ? shouldFireRitual(scope, propertyId, config) : false,
  )

  // Kick off a background config fetch once per session
  useEffect(() => { fetchConfigOnce() }, [])

  // Mark the day's ritual as seen once the genuine load completes — no
  // artificial delay; readiness drives the surface entirely.
  useEffect(() => {
    if (!ritual || !isReady || markedRef.current) return
    if (propertyId) markSeen(scope, propertyId)
    markedRef.current = true
  }, [ritual, isReady, scope, propertyId])

  return { showRitual: ritual && !isReady }
}
